// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { SlTpHolder } from '@shared/utils/Trading/SlTpHolder';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { Resources } from '@shared/localizations/Resources';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { OrderTif, OrderTifMap } from '@shared/utils/Trading/OrderTifEnum';
import { OptionPutCall } from '@shared/utils/Instruments/OptionPutCall';
import { DataCache } from '../../DataCache';
import { MathUtils } from '@shared/utils/MathUtils';
import { EventEmitter } from 'events';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { LinkedPriceType } from './SavedOrderEnums';
import { TradingOrderStatus } from '../../Trading/TradingOrderStatus';
import { BaseSettings } from '../../Settings/BaseGeneralSettingsWrapper';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { StringUtils } from '@shared/utils/StringUtils';
export class SavedOrder {
    constructor(id) {
        this._eventEmitter = new EventEmitter();
        this.Active = false;
        this.InstrumentName = '';
        this.AccountName = '';
        this.Operation = OperationType.Buy;
        this.Cash = 0;
        this.QuantityLots = 0;
        this.DisclosedQuantityLots = 0;
        this.OrderType = OrderType.Limit;
        this.TIF = OrderTif.Day;
        this.TIFExpiration = null;
        this.LinkedPriceType = LinkedPriceType.None;
        this.LinkedPriceOffset = 0;
        this.Price = NaN;
        this.StopPrice = NaN;
        this.SLTPHolder = new SlTpHolder();
        this.Status = TradingOrderStatus.None;
        this.StatusError = null;
        this.ProductType = BaseSettings.productType;
        this.Leverage = null;
        this._userComment = null;
        this.IsAllowOTHTrading = null;
        this.Id = id;
    }
    get Instrument() {
        return this._instrument;
    }
    set Instrument(value) {
        const hasSubscribers = this.hasSubscribers();
        if (!MathUtils.IsNullOrUndefined(this.Instrument) && hasSubscribers) {
            this.unsubscribe();
        }
        this._instrument = value;
        if (!MathUtils.IsNullOrUndefined(this._instrument)) {
            this.InstrumentName = this._instrument.DisplayName();
        }
        this.updateProductType();
        this.updateLeverage();
        if (hasSubscribers) {
            this.subscribe();
        }
        this._eventEmitter.emit('updateInstrument');
    }
    get Account() {
        return this._account;
    }
    set Account(value) {
        this._account = value;
        if (!MathUtils.IsNullOrUndefined(this._account)) {
            this.AccountName = this._account.toString();
        }
        this.updateProductType();
        this.updateLeverage();
    }
    get AccountId() {
        if (MathUtils.IsNullOrUndefined(this.Account)) {
            return '';
        }
        return this.Account.userPin;
    }
    get AccountStr() {
        if (MathUtils.IsNullOrUndefined(this.Account)) {
            return this.AccountName;
        }
        return this.Account.toString();
    }
    get InstrumentStr() {
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return this.InstrumentName;
        }
        return this.Instrument.DisplayName();
    }
    get InstrumentTypeStr() {
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return '';
        }
        return InstrumentUtils.getInstrumentTypeStringLocalized(this.Instrument);
    }
    get CashStr() {
        var _a, _b;
        return (_b = (_a = this.Account) === null || _a === void 0 ? void 0 : _a.formatPrice(this.Cash)) !== null && _b !== void 0 ? _b : this.Cash.toString();
    }
    get OperationStr() {
        return Resources.getResource(`general.trading.${OrderUtils.getBuySellStr(this.Operation)}`);
    }
    get OrderTypeStr() {
        return Resources.getResource(`property.${OrderUtils.getOrderTypeLocalizationKey(this.OrderType)}`);
    }
    get TifStr() {
        if (isNullOrUndefined(this.TIF)) {
            return '';
        }
        const tif = Resources.getResource(`property.${OrderTifMap[this.TIF]}`);
        let additionalString = '';
        if (this.TIF === OrderTif.GTD && !isNullOrUndefined(this.TIFExpiration)) {
            additionalString = ` ${DateTimeUtils.FormatToDate(this.TIFExpiration)}`;
        }
        return Resources.getResource(`${tif}${additionalString}`);
    }
    get BasePrice() {
        return this.getBasePrice();
    }
    get Bid() {
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return NaN;
        }
        return this.Instrument.Level1.GetBid(this.Account);
    }
    get Ask() {
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return NaN;
        }
        return this.Instrument.Level1.GetAsk(this.Account);
    }
    get Last() {
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return NaN;
        }
        return this.Instrument.Level1.GetLastPrice(this.Account);
    }
    get TradingExchange() {
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return '';
        }
        return this.Instrument.TradingExchange;
    }
    get Route() {
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return '';
        }
        return this.Instrument.routeName;
    }
    get SymbolType() {
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return InstrumentTypes.GENERAL;
        }
        return this.Instrument.InstrType;
    }
    get SymbolTypeStr() {
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return InstrumentUtils.getInstrumentTypeStringLocalized(this.SymbolType);
        }
        else {
            return this.Instrument.getTypeString();
        }
    }
    get SlPrice() {
        if (isNullOrUndefined(this.Instrument)) {
            return NaN;
        }
        if (this.SLTPHolder.isSlEmpty()) {
            return NaN;
        }
        return SlTpUtils.getSLTPPrice(this.Instrument, this.Operation === OperationType.Buy, true, this.getBasePrice(), this.SLTPHolder.StopLossPriceValue, this.SLTPHolder.StopLossPriceType);
    }
    get SllPrice() {
        if (isNullOrUndefined(this.Instrument)) {
            return NaN;
        }
        if (this.SLTPHolder.isSlEmpty() || this.SLTPHolder.isSllEmpty()) {
            return NaN;
        }
        return SlTpUtils.getSLLPrice(this.Instrument, this.Operation === OperationType.Buy, this.getBasePrice(), this.SLTPHolder.StopLossLimitPriceValue, this.SLTPHolder.StopLossLimitPriceValue, this.SLTPHolder.StopLossPriceType);
    }
    get TpPrice() {
        if (isNullOrUndefined(this.Instrument)) {
            return NaN;
        }
        if (this.SLTPHolder.isTpEmpty()) {
            return NaN;
        }
        return SlTpUtils.getSLTPPrice(this.Instrument, this.Operation === OperationType.Buy, false, this.getBasePrice(), this.SLTPHolder.TakeProfitPriceValue, this.SLTPHolder.TakeProfitPriceType);
    }
    get SlOffset() {
        if (isNullOrUndefined(this.Instrument)) {
            return NaN;
        }
        if (this.SLTPHolder.isSlEmpty()) {
            return NaN;
        }
        return SlTpUtils.getSLTPOffset(this.Instrument, this.getBasePrice(), this.SLTPHolder.StopLossPriceValue, this.SLTPHolder.StopLossPriceType);
    }
    get SllOffset() {
        if (isNullOrUndefined(this.Instrument)) {
            return NaN;
        }
        if (this.SLTPHolder.isSlEmpty() || this.SLTPHolder.isSllEmpty()) {
            return NaN;
        }
        return SlTpUtils.getSLLOffset(this.Instrument, this.SLTPHolder.StopLossPriceValue, this.SLTPHolder.StopLossLimitPriceValue, this.SLTPHolder.StopLossPriceType);
    }
    get TpOffset() {
        if (isNullOrUndefined(this.Instrument)) {
            return NaN;
        }
        if (this.SLTPHolder.isTpEmpty()) {
            return NaN;
        }
        return SlTpUtils.getSLTPOffset(this.Instrument, this.getBasePrice(), this.SLTPHolder.TakeProfitPriceValue, this.SLTPHolder.TakeProfitPriceType);
    }
    get OptionsTypeStr() {
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return '';
        }
        else if (this.Instrument.InstrType === InstrumentTypes.OPTIONS) {
            return this.Instrument.PutCall === OptionPutCall.OPTION_CALL_VANILLA ? 'Call' : 'Put';
        }
        else {
            return '';
        }
    }
    get StrikePrice() {
        if (isNullOrUndefined(this.Instrument) || this.Instrument.InstrType !== InstrumentTypes.OPTIONS) {
            return undefined;
        }
        else {
            return this.Instrument.StrikePrice;
        }
    }
    get StrikePriceStr() {
        if (isNullOrUndefined(this.Instrument) || this.Instrument.InstrType !== InstrumentTypes.OPTIONS) {
            return '';
        }
        const strikePriceStr = this.Instrument.formatPrice(this.Instrument.StrikePrice);
        const strikeVersionStr = isValidString(this.Instrument.StrikeVersion)
            ? ` (${this.Instrument.StrikeVersion})`
            : '';
        return `${strikePriceStr}${strikeVersionStr}`;
    }
    get ExpirityDateStr() {
        const instrument = this.Instrument;
        if (MathUtils.IsNullOrUndefined(instrument)) {
            return '';
        }
        else if ((instrument.isFutureOrOption() || instrument.InstrType === InstrumentTypes.FORWARD) &&
            instrument.ExpDateReal.getFullYear() > 2000) {
            return DateTimeUtils.FormatToDate(DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(instrument.ExpDateReal));
        }
        else {
            return '';
        }
    }
    get ProductTypeStr() {
        return InstrumentUtils.GetLocalizedProductType(this.Instrument, this.ProductType);
    }
    get LegerageStr() {
        if (this.Leverage === null) {
            return StringUtils.NOT_AVAILABLE;
        }
        else {
            return this.Leverage.toString();
        }
    }
    get StatusStr() {
        switch (this.Status) {
            case TradingOrderStatus.None:
                return '';
            case TradingOrderStatus.CancelledByUser:
                return Resources.getResource('general.trading.orderStatus.CancelledByUser');
            case TradingOrderStatus.Placing:
                return Resources.getResource('general.trading.orderStatus.PlacingOrder');
            case TradingOrderStatus.IllegalParameters:
                return Resources.getResource('general.trading.orderStatus.IllegalParameters');
            case TradingOrderStatus.Opened:
                return `${Resources.getResource('reports.Order')} ${this.OpenedId} ${Resources.getResource('general.trading.orderStatus.Opened')}`;
            case TradingOrderStatus.Filled:
                return `${Resources.getResource('reports.Position')} ${this.OpenedId} ${Resources.getResource('general.trading.orderStatus.Opened')}`;
            case TradingOrderStatus.Error:
                return `${Resources.getResource('property.Error')}: ${this.StatusError}`;
            default:
                return '';
        }
    }
    set UserComment(value) {
        this._userComment = value;
    }
    get UserComment() {
        var _a;
        if ((_a = this._userComment) === null || _a === void 0 ? void 0 : _a.isEmpty()) {
            return '';
        }
        else {
            return this._userComment;
        }
    }
    hasSubscribers() {
        return this._eventEmitter.listenerCount('update') > 0;
    }
    subscribe() {
        const instrument = this.Instrument;
        if (MathUtils.IsNullOrUndefined(instrument)) {
            return;
        }
        const quoteCache = DataCache.FQuoteCache;
        quoteCache.addListener(instrument, this, HistoryType.QUOTE_LEVEL1);
        quoteCache.addListener(instrument, this, HistoryType.QUOTE_TRADES);
    }
    unsubscribe() {
        const instrument = this.Instrument;
        if (MathUtils.IsNullOrUndefined(instrument)) {
            return;
        }
        const quoteCache = DataCache.FQuoteCache;
        quoteCache.removeListener(instrument, this, HistoryType.QUOTE_LEVEL1);
        quoteCache.removeListener(instrument, this, HistoryType.QUOTE_TRADES);
    }
    getBasePrice() {
        switch (this.OrderType) {
            case OrderType.Stop:
                return this.StopPrice;
            case OrderType.TrailingStop:
                return this.Instrument.calculatePrice(this.Operation === OperationType.Buy ? this.Ask : this.Bid, this.StopPrice * (this.Operation === OperationType.Buy ? 1 : -1), true);
            case OrderType.Market:
                return this.Operation === OperationType.Buy ? this.Ask : this.Bid;
            default:
                return this.Price;
        }
    }
    updateProductType() {
        if (isNullOrUndefined(this.Account)) {
            return;
        }
        if (isNullOrUndefined(this.Instrument)) {
            return;
        }
        const availableProductTypes = this.Account.RiskPlan !== null && this.Account.RiskPlan !== undefined
            ? this.Account.RiskPlan.GetRisksForInstrument(this.Instrument.GetInteriorID()).availableProductTypes
            : InstrumentUtils.getAllowedProductTypeDict(this.Instrument);
        if (isValidArray(availableProductTypes)) {
            if (isNullOrUndefined(this.ProductType) || !availableProductTypes.includes(this.ProductType)) {
                this.ProductType = availableProductTypes[0];
            }
        }
        else {
            this.ProductType = ProductType.General;
        }
    }
    updateLeverage() {
        if (MathUtils.IsNullOrUndefined(this.Account)) {
            return;
        }
        if (MathUtils.IsNullOrUndefined(this.Instrument)) {
            return;
        }
        const isLeverageVisible = this.Instrument.isLeverageVisible(this.Account, this.ProductType);
        if (isLeverageVisible) {
            const leverages = this.Instrument.getLeverages(this.Account, this.ProductType);
            if (this.Leverage === null) {
                this.Leverage = leverages[0];
            }
        }
        else {
            this.Leverage = null;
        }
    }
    formatPrice(price) {
        if (MathUtils.IsNullOrUndefined(this.Instrument) || isNaN(price) || price === null) {
            return '';
        }
        else {
            return this.Instrument.formatPrice(price);
        }
    }
    formatSLOffset(visualOffset) {
        if (isNullOrUndefined(this.Instrument) || !isValidNumber(visualOffset)) {
            return '';
        }
        else {
            const offsetType = SlTpUtils.getSLVisualOffsetType(this.Instrument, this.SLTPHolder);
            const formattedOffset = SlTpUtils.formatSLTPValue(visualOffset, this.Instrument, offsetType);
            return `${formattedOffset} ${SlTpUtils.getTextForOffsetMode(offsetType)}`;
        }
    }
    formatTPOffset(visualOffset) {
        if (isNullOrUndefined(this.Instrument) || !isValidNumber(visualOffset)) {
            return '';
        }
        else {
            const offsetType = SlTpUtils.getTPVisualOffsetType(this.Instrument, this.SLTPHolder);
            const formattedOffset = SlTpUtils.formatSLTPValue(visualOffset, this.Instrument, offsetType);
            return `${formattedOffset} ${SlTpUtils.getTextForOffsetMode(offsetType)}`;
        }
    }
    formatTicksOffset(visualTicksOffset) {
        if (isNullOrUndefined(this.Instrument) || !isValidNumber(visualTicksOffset)) {
            return '';
        }
        else {
            const offsetType = SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(this.Instrument);
            return SlTpUtils.formatSLTPValue(visualTicksOffset, this.Instrument, offsetType);
        }
    }
    formatAmount(amount) {
        if (MathUtils.IsNullOrUndefined(amount)) {
            return '';
        }
        else if (this.Instrument === null || this.Instrument === undefined) {
            return amount.toString();
        }
        else {
            return InstrumentUtils.formatAmountValue(amount, this.Instrument, this.Account, this.ProductType);
        }
    }
    setDefaultPrices() {
        if (isNullOrUndefined(this.Instrument)) {
            return;
        }
        this.Price = NaN;
        this.StopPrice = NaN;
        switch (this.OrderType) {
            case OrderType.Limit:
                this.Price = this.Instrument.GetMarketPrice(this.Account, this.Operation);
                if (!isValidNumber(this.Price)) {
                    this.Price = this.Instrument.GetPointSize(null);
                }
                break;
            case OrderType.Stop:
                this.StopPrice = this.Instrument.GetMarketPrice(this.Account, this.Operation);
                if (!isValidNumber(this.StopPrice)) {
                    this.StopPrice = this.Instrument.GetPointSize(null);
                }
                break;
            case OrderType.StopLimit:
            case OrderType.OCO:
                this.Price = this.Instrument.GetMarketPrice(this.Account, this.Operation);
                if (!isValidNumber(this.Price)) {
                    this.Price = this.Instrument.GetPointSize(null);
                }
                this.StopPrice = this.Instrument.GetMarketPrice(this.Account, this.Operation);
                if (!isValidNumber(this.StopPrice)) {
                    this.StopPrice = this.Instrument.GetPointSize(null);
                }
                break;
            case OrderType.TrailingStop:
                this.StopPrice = 1;
                break;
        }
        this._eventEmitter.emit('onUpdateDefaultPrices');
    }
    setLinkedPrice() {
        if (this.LinkedPriceType === LinkedPriceType.None) {
            return;
        }
        let linkedPrice;
        switch (this.LinkedPriceType) {
            case LinkedPriceType.Bid:
                linkedPrice = this.Bid;
                break;
            case LinkedPriceType.Ask:
                linkedPrice = this.Ask;
                break;
            case LinkedPriceType.Last:
                linkedPrice = this.Last;
                break;
            default:
                linkedPrice = undefined;
                break;
        }
        if (linkedPrice === undefined) {
            return;
        }
        switch (this.OrderType) {
            case OrderType.Limit:
                this.Price = this.Instrument.calculatePrice(linkedPrice, this.LinkedPriceOffset, true);
                break;
            case OrderType.Stop:
                this.StopPrice = this.Instrument.calculatePrice(linkedPrice, this.LinkedPriceOffset, true);
                break;
        }
    }
    SubscribeOnUpdate(callback) {
        const isSubscribed = this.hasSubscribers();
        this._eventEmitter.on('update', callback);
        if (!isSubscribed) {
            this.subscribe();
        }
    }
    SubscribeOnUpdateInstrument(callback) {
        this._eventEmitter.on('updateInstrument', callback);
    }
    SubscribeOnUpdateDefaultPrices(callback) {
        this._eventEmitter.on('onUpdateDefaultPrices', callback);
    }
    UnSubscribeOnUpdateDefaultPrices(callback) {
        this._eventEmitter.off('onUpdateDefaultPrices', callback);
    }
    UnsubscribeOnUpdate(callback) {
        this._eventEmitter.off('update', callback);
        if (!this.hasSubscribers()) {
            this.unsubscribe();
        }
    }
    UnsubscribeOnUpdateInstrument(callback) {
        this._eventEmitter.off('updateInstrument', callback);
    }
    newQuote() {
        this._eventEmitter.emit('update', this);
        this.setLinkedPrice();
    }
}
